/** @jsxImportSource preact */
import { h, render } from 'preact';
import { RiskSubdomainChart, getChartController } from './components/RiskSubdomainChart';
import { CategoryBarChart } from './components/CategoryBarChart';
import { DocumentView } from './components/DocumentView';
import { IncidentChart } from './components/IncidentChart';
import { CHART_CONFIGS, INCIDENT_CHART_CONFIGS } from './data/chartConfigs';
import type { StackByOption } from './components/FilterControls';
import type { ViewMode } from './components/ViewModeToggle';
import type { ActorRole } from './data/chartConfigs';

// Inject CSS variables for theming
function injectStyles() {
  if (document.getElementById('govcharts-styles')) return;

  const style = document.createElement('style');
  style.id = 'govcharts-styles';
  style.textContent = `
    /* Reset inherited styles inside chart containers */
    [id^="govcharts-"], [id="risk-chart"] {
      line-height: 1.4;
      font-size: 14px;
      box-sizing: border-box;
    }
    [id^="govcharts-"] *, [id="risk-chart"] * {
      line-height: inherit;
      box-sizing: border-box;
      margin: 0;
    }
    [id^="govcharts-"] button, [id="risk-chart"] button {
      font-family: inherit;
      line-height: 1.4;
    }
    [id^="govcharts-"] label, [id="risk-chart"] label {
      font-weight: normal;
      margin-bottom: 0;
    }
    [id^="govcharts-"] input[type="checkbox"], [id="risk-chart"] input[type="checkbox"] {
      width: auto;
      height: auto;
      margin: 0;
    }

    :root {
      --gc-background: transparent;
      --gc-surface: inherit;
      --gc-surface-muted: #f1f5f9;
      --gc-border: #e5e7eb;
      --gc-text-muted: #6b7280;
      --gc-button-bg: #ffffff;
      --gc-button-bg-active: #e5e7eb;
      --gc-button-text: #111827;
      --gc-button-text-active: #111827;
    }
  `;
  document.head.appendChild(style);
}

// Chart type definitions
type ChartType =
  | 'risk-domain-bar'
  | 'risk-subdomain-bar'
  | 'sectors-covered-bar'
  | 'lifecycle-stages-bar'
  | 'technical-scope-bar'
  | 'legislative-status-bar'
  | 'actors-bar'
  | 'coverage-heatmap'
  | 'incident-timeline'
  | 'incident-domain-year'
  | 'incident-domain-subdomain'
  | 'incident-domain-pie'
  | 'incident-subdomain-year'
  | 'document-view';

interface InitOptions {
  type: ChartType;
  container: string | HTMLElement;
  apiBase?: string;
  filters?: Record<string, string>;
  stackBy?: StackByOption;
  viewMode?: ViewMode;
}

interface ChartInstance {
  destroy: () => void;
  applyFilters: (filters: ApplyFiltersOptions) => void;
}

interface ApplyFiltersOptions {
  stackBy?: StackByOption;
  coverage?: string | string[];
  jurisdiction?: string | string[];
  authority?: string | string[];
  legislativeStatus?: string | string[];
  activity?: string | string[];
  appliesTo?: string | string[];
  lifecycleStage?: string | string[];
  technicalScope?: string | string[];
  riskSubdomain?: string | string[];
  sector?: string | string[];
  actorRole?: ActorRole;
  viewMode?: ViewMode;
}

// Active instances
const instances: Map<string, { unmount: () => void }> = new Map();

// Default API base (can be overridden per chart)
let defaultApiBase = '';

// Auto-detect API base from script src
function detectApiBase(): string {
  if (defaultApiBase) return defaultApiBase;

  // Try to detect from script tag
  const scripts = document.getElementsByTagName('script');
  for (let i = 0; i < scripts.length; i++) {
    const src = scripts[i].src;
    if (src && src.includes('embed.js')) {
      try {
        const url = new URL(src);
        return `${url.protocol}//${url.host}/api`;
      } catch {
        // Ignore parsing errors
      }
    }
  }

  // Fallback to current origin
  return `${window.location.protocol}//${window.location.host}/api`;
}

/**
 * Configure global settings
 */
function configure(options: { apiBase?: string }): void {
  if (options.apiBase) {
    defaultApiBase = options.apiBase;
  }
}

/**
 * Initialise a chart
 */
function init(options: InitOptions): ChartInstance {
  const {
    type,
    container,
    apiBase = detectApiBase(),
    filters,
    stackBy,
    viewMode,
  } = options;

  // Inject styles on first init
  injectStyles();

  // Resolve container element
  const el =
    typeof container === 'string'
      ? document.getElementById(container)
      : container;

  if (!el) {
    throw new Error(`GovCharts: Container not found: ${container}`);
  }

  // Generate ID if needed
  const id = el.id || `govcharts-${Date.now()}`;
  if (!el.id) el.id = id;

  // Destroy existing instance if any
  if (instances.has(id)) {
    instances.get(id)?.unmount();
    instances.delete(id);
  }

  // Render appropriate chart type
  if (type === 'risk-subdomain-bar') {
    render(
      <RiskSubdomainChart
        apiBase={apiBase}
        containerId={id}
        initialFilters={filters}
        initialStackBy={stackBy}
        initialViewMode={viewMode}
      />,
      el
    );
  } else if (type === 'document-view') {
    render(
      <DocumentView
        apiBase={apiBase}
        containerId={id}
        initialFilters={filters}
      />,
      el
    );
  } else if (INCIDENT_CHART_CONFIGS[type]) {
    render(
      <IncidentChart
        config={INCIDENT_CHART_CONFIGS[type]}
        apiBase={apiBase}
        containerId={id}
      />,
      el
    );
  } else if (CHART_CONFIGS[type]) {
    render(
      <CategoryBarChart
        config={CHART_CONFIGS[type]}
        apiBase={apiBase}
        containerId={id}
        initialFilters={filters}
        initialStackBy={stackBy}
        initialViewMode={viewMode}
      />,
      el
    );
  } else {
    throw new Error(`GovCharts: Unknown or not yet implemented chart type: ${type}`);
  }

  // Store instance with unmount function
  const instance = {
    unmount: () => {
      render(null, el);
    },
  };
  instances.set(id, instance);

  // Return public API for this instance
  return {
    destroy: () => destroy(id),
    applyFilters: (filters: ApplyFiltersOptions) => applyFilters(id, filters),
  };
}

/**
 * Apply filters to a chart from external code (e.g. hyperlinks on the page)
 */
function applyFilters(containerId: string, filters: ApplyFiltersOptions): void {
  const controller = getChartController(containerId);
  if (!controller) {
    console.warn(`GovCharts.applyFilters: No chart found with id "${containerId}"`);
    return;
  }

  // Reset all filters to defaults first, then apply the specified ones
  controller.resetFilters();

  if (filters.stackBy !== undefined) controller.setStackBy(filters.stackBy);
  if (filters.coverage !== undefined) controller.setCoverageFilter(filters.coverage);
  if (filters.jurisdiction !== undefined) controller.setJurisdictionFilter(filters.jurisdiction);
  if (filters.authority !== undefined) controller.setAuthorityFilter(filters.authority);
  if (filters.legislativeStatus !== undefined) controller.setLegislativeStatusFilter(filters.legislativeStatus);
  if (filters.activity !== undefined) controller.setActivityFilter(filters.activity);
  if (filters.appliesTo !== undefined) controller.setAppliesToFilter(filters.appliesTo);
  if (filters.lifecycleStage !== undefined) controller.setLifecycleStageFilter(filters.lifecycleStage);
  if (filters.technicalScope !== undefined) controller.setTechnicalScopeFilter(filters.technicalScope);
  if (filters.riskSubdomain !== undefined) controller.setRiskSubdomainFilter(filters.riskSubdomain);
  if (filters.sector !== undefined) controller.setSectorFilter(filters.sector);
  if (filters.actorRole !== undefined) controller.setActorRole(filters.actorRole);
  if (filters.viewMode !== undefined) controller.setViewMode(filters.viewMode);
}

/**
 * Destroy a chart instance
 */
function destroy(containerId: string): void {
  const instance = instances.get(containerId);
  if (instance) {
    instance.unmount();
    instances.delete(containerId);
  }
}

/**
 * Destroy all chart instances
 */
function destroyAll(): void {
  instances.forEach((instance) => instance.unmount());
  instances.clear();
}

// Public API
const GovCharts = {
  configure,
  init,
  applyFilters,
  destroy,
  destroyAll,
  version: '2.0.0',
};

// Expose globally
declare global {
  interface Window {
    GovCharts: typeof GovCharts;
  }
}

window.GovCharts = GovCharts;

export default GovCharts;
